
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import { Search, Mail, Phone, Loader2, Users, Bot, Sparkles, MessageSquare } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllEmployees, getDepartments, getTeamOut, type Employee as ApiEmployee } from '@/lib/hrApi';
import { DANA, danaMatchesSearch, openDana } from '@/lib/dana';

interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  email: string;
  phone: string;
  status: 'active' | 'on_leave' | 'inactive';
  avatar?: string;
  isAssistant?: boolean;
}

const DANA_ENTRY: Employee = {
  id: DANA.id,
  name: DANA.name,
  position: DANA.position,
  department: DANA.department,
  email: DANA.email,
  phone: '',
  status: 'active',
  isAssistant: true,
};

// Map the backend status values onto the UI's status union (same as desktop).
const mapStatus = (s?: string): Employee['status'] => {
  switch ((s || '').toLowerCase()) {
    case 'on_leave': return 'on_leave';
    case 'inactive':
    case 'terminated':
    case 'suspended': return 'inactive';
    default: return 'active';
  }
};

export const MobileEmployeeDirectory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [deptRes, emps, outToday] = await Promise.all([
          getDepartments().catch(() => ({ items: [] as any[] })),
          getAllEmployees(),
          getTeamOut(0).catch(() => [] as any[]),
        ]);
        if (!active) return;
        const deptMap = new Map<string, string>((deptRes.items || []).map((d: any) => [d.id, d.name]));
        const outSet = new Set<string>((outToday as any[]).map((o) => String(o.employee_id)));
        const mapped: Employee[] = (emps as ApiEmployee[]).map((e) => {
          const onLeaveNow = !!e.user_id && outSet.has(String(e.user_id));
          return {
            id: e.id,
            name: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email,
            position: e.position || '—',
            department: (e.department_id && deptMap.get(e.department_id)) || 'Unassigned',
            email: e.email,
            phone: e.phone || '',
            status: onLeaveNow ? 'on_leave' : mapStatus(e.status),
            avatar: e.profile_picture_url || undefined,
          };
        });
        setEmployees(mapped);
        setError(null);
      } catch (err: any) {
        if (active) setError(err?.response?.data?.detail || err?.message || 'Failed to load the directory');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const getStatusColor = (status: Employee['status']) => {
    switch (status) {
      case 'active': return 'bg-success-soft text-success-strong';
      case 'on_leave': return 'bg-warning-soft text-warning-strong';
      case 'inactive': return 'bg-danger-soft text-danger-strong';
      default: return 'bg-muted text-foreground';
    }
  };

  const goToEmployee = (id: string) => {
    const base = location.pathname.replace(/\/employees(\/.*)?$/, '/employees');
    navigate(`${base}/${id}`);
  };

  const filteredEmployees = employees.filter((employee) =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.department.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  // Pin Dana at the top (she isn't part of the loaded employee list).
  const listed = danaMatchesSearch(searchTerm) ? [DANA_ENTRY, ...filteredEmployees] : filteredEmployees;

  return (
    <div className="max-w-md mx-auto space-y-4 p-4 md:hidden">
      {/* Mobile Search Header */}
      <div className="sticky top-0 bg-card z-10 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-orange-600">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Loading directory…</span>
        </div>
      ) : error ? (
        <div className="p-6 bg-danger-soft border border-danger rounded-lg text-center">
          <p className="text-sm text-danger-strong">{error}</p>
        </div>
      ) : listed.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">
            {searchTerm
              ? `No employees match “${searchTerm}”.`
              : employees.length === 0
                ? 'No employees yet.'
                : 'No employees match your search.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {listed.map((employee) => (
            <Card
              key={employee.id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${employee.isAssistant ? 'border-2 border-orange-200 dark:border-orange-500/30' : ''}`}
              onClick={() => goToEmployee(employee.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {employee.isAssistant ? (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                    ) : (
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={employee.avatar} />
                        <AvatarFallback>
                          {employee.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-1.5">
                        {employee.name}
                        {employee.isAssistant && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-600 bg-orange-100 dark:bg-orange-500/[0.16] rounded-full px-1.5 py-0.5">
                            <Sparkles className="w-2.5 h-2.5" /> AI
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground">{employee.position}</p>
                      <p className="text-xs text-muted-foreground">{employee.department}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Badge className={employee.isAssistant ? 'bg-orange-100 text-orange-700' : getStatusColor(employee.status)}>
                    {employee.isAssistant ? 'Always available' : employee.status.replace('_', ' ')}
                  </Badge>

                  <div className="flex gap-1">
                    {employee.isAssistant ? (
                      <Button
                        size="sm"
                        className="p-2 bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={(e) => { e.stopPropagation(); openDana(); }}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    ) : (
                      <>
                        {employee.email && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `mailto:${employee.email}`;
                            }}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        {employee.phone && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `tel:${employee.phone}`;
                            }}
                          >
                            <Phone className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
